import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CheckCircle as ResolvedIcon,
  RateReview as ReviewedIcon,
  AttachFile as AttachFileIcon,
  Reply as ReplyIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  MarkEmailUnread as UnreadIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import {
  PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { adminAPI } from '../services/api';

const STATUS_COLORS = {
  new: 'info',
  reviewed: 'warning',
  resolved: 'success',
  archived: 'default',
};

const TYPE_LABELS = {
  bug: 'Bug',
  suggestion: 'Suggestion',
  question: 'Question',
  compliment: 'Compliment',
  other: 'Other',
};

const REASON_LABELS = {
  too_expensive: 'Too Expensive',
  missing_features: 'Missing Features',
  switched_service: 'Switched Service',
  unused: 'Not Using It',
  customer_service: 'Customer Service',
  too_complex: 'Too Complex',
  other: 'Other',
  not_specified: 'Not Specified',
};

const PIE_COLORS = ['#1976d2', '#f44336', '#ff9800', '#4caf50', '#9c27b0', '#00bcd4', '#795548', '#607d8b'];

const Feedback = () => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [adminResponse, setAdminResponse] = useState('');
  const [updating, setUpdating] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Cancellation analytics state
  const [timeRange, setTimeRange] = useState(30);
  const [cancellationReasons, setCancellationReasons] = useState([]);
  const [cancellationTrend, setCancellationTrend] = useState([]);
  const [cancellationEntries, setCancellationEntries] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all');

  const loadFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;

      const res = await adminAPI.getGeneralFeedback(params);
      setFeedback(res.data.feedback);
      setTotal(res.data.pagination.total);
      window.dispatchEvent(new Event('feedback-updated'));
    } catch (err) {
      setError('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterType, filterStatus]);

  const loadCancellationAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      const [analyticsRes, feedbackRes] = await Promise.all([
        adminAPI.getCancellationAnalytics({ days: timeRange }),
        adminAPI.getCancellationFeedback({ days: timeRange, limit: 50 }),
      ]);
      setCancellationReasons(analyticsRes.data.reasons || []);
      setCancellationTrend(analyticsRes.data.trend || []);
      setCancellationEntries(feedbackRes.data.entries || []);
    } catch (err) {
      console.error('Failed to load cancellation analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  useEffect(() => {
    loadCancellationAnalytics();
  }, [loadCancellationAnalytics]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      setUpdating(true);
      await adminAPI.updateGeneralFeedback(id, { status: newStatus });
      loadFeedback();
      window.dispatchEvent(new Event('feedback-updated'));
      if (selectedFeedback?.id === id) {
        setSelectedFeedback({ ...selectedFeedback, status: newStatus });
      }
    } catch (err) {
      setError('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedFeedback) return;
    try {
      setUpdating(true);
      await adminAPI.updateGeneralFeedback(selectedFeedback.id, { admin_notes: adminNotes });
      loadFeedback();
      setSelectedFeedback({ ...selectedFeedback, admin_notes: adminNotes });
    } catch (err) {
      setError('Failed to save notes');
    } finally {
      setUpdating(false);
    }
  };

  const openDetail = (item) => {
    setSelectedFeedback(item);
    setAdminNotes(item.admin_notes || '');
    setAdminResponse(item.admin_response || '');
    setUploadFiles([]);
  };

  const handleSaveResponse = async () => {
    if (!selectedFeedback) return;
    try {
      setUpdating(true);
      await adminAPI.updateGeneralFeedback(selectedFeedback.id, { admin_response: adminResponse });
      loadFeedback();
      window.dispatchEvent(new Event('feedback-updated'));
      setSelectedFeedback({ ...selectedFeedback, admin_response: adminResponse, responded_at: adminResponse ? new Date().toISOString() : null });
    } catch (err) {
      setError('Failed to save response');
    } finally {
      setUpdating(false);
    }
  };

  const handleAttachmentClick = async (feedbackId, attachment) => {
    try {
      const res = await adminAPI.getAdminFeedbackAttachmentUrl(feedbackId, attachment.key);
      window.open(res.data.url, '_blank');
    } catch (err) {
      setError('Failed to load attachment');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const handleUploadAttachments = async () => {
    if (!selectedFeedback || uploadFiles.length === 0) return;
    try {
      setUploading(true);
      const formData = new FormData();
      uploadFiles.forEach(file => formData.append('attachments', file));
      const res = await adminAPI.uploadFeedbackAttachments(selectedFeedback.id, formData);
      setSelectedFeedback({ ...selectedFeedback, admin_attachments: res.data.attachments });
      setUploadFiles([]);
      loadFeedback();
      window.dispatchEvent(new Event('feedback-updated'));
    } catch (err) {
      setError('Failed to upload attachments');
    } finally {
      setUploading(false);
    }
  };

  const isImageType = (contentType) => contentType && contentType.startsWith('image/');

  // Build merged grid data
  const getMergedFeedback = () => {
    const activeFeedback = feedback.map(item => ({
      ...item,
      source: 'active',
      displayMessage: item.message,
      displayDate: item.created_at,
    }));

    const cancellationFeedbackItems = cancellationEntries.map(entry => ({
      id: `cancel-${entry.id}`,
      source: 'cancellation',
      type: 'cancellation',
      displayMessage: entry.cancellation_feedback || entry.cancellation_reason || 'No feedback provided',
      user_name: entry.name,
      user_email: entry.email,
      displayDate: entry.canceled_at,
      status: 'resolved',
      cancellation_reason: entry.cancellation_reason,
      subscription_started_at: entry.subscription_started_at,
      canceled_at: entry.canceled_at,
    }));

    if (sourceFilter === 'active') return activeFeedback;
    if (sourceFilter === 'cancellation') return cancellationFeedbackItems;
    return [...activeFeedback, ...cancellationFeedbackItems].sort(
      (a, b) => new Date(b.displayDate) - new Date(a.displayDate)
    );
  };

  const computeTenure = (startedAt, canceledAt) => {
    if (!startedAt || !canceledAt) return null;
    const start = new Date(startedAt);
    const end = new Date(canceledAt);
    const days = Math.floor((end - start) / (1000 * 60 * 60 * 24));
    if (days < 30) return `${days}d`;
    const months = Math.floor(days / 30);
    return `${months}mo`;
  };

  const totalCancellations = cancellationReasons.reduce((sum, r) => sum + r.count, 0);

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        Feedback
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Time Range Filter */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3, overflowX: 'auto' }}>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(e, val) => { if (val !== null) setTimeRange(val); }}
          size="small"
          sx={{ whiteSpace: 'nowrap' }}
        >
          <ToggleButton value={7}>Last 7 Days</ToggleButton>
          <ToggleButton value={30}>Last 30 Days</ToggleButton>
          <ToggleButton value={90}>Last 90 Days</ToggleButton>
          <ToggleButton value={0}>All Time</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Cancellation Analytics Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Donut Chart - Cancellation Reasons */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight={600} fontSize={16}>
              Cancellation Reasons
              {totalCancellations > 0 && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({totalCancellations} total)
                </Typography>
              )}
            </Typography>
            {analyticsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                <CircularProgress />
              </Box>
            ) : cancellationReasons.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                <Typography color="text.secondary">No cancellations in this period</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={cancellationReasons}
                    dataKey="count"
                    nameKey="reason"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={50}
                    label={({ reason, count }) => `${REASON_LABELS[reason] || reason}: ${count}`}
                    labelLine={true}
                  >
                    {cancellationReasons.map((entry, index) => (
                      <Cell key={entry.reason} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value, name) => [value, REASON_LABELS[name] || name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Line Chart - Cancellation Trend */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight={600} fontSize={16}>
              Cancellation Trend
            </Typography>
            {analyticsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                <CircularProgress />
              </Box>
            ) : cancellationTrend.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                <Typography color="text.secondary">No cancellations in this period</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={cancellationTrend}>
                  <XAxis
                    dataKey="date"
                    tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    fontSize={12}
                  />
                  <YAxis allowDecimals={false} fontSize={12} />
                  <RechartsTooltip
                    labelFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#f44336"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Cancellations"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Source Filter + Existing Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup
          value={sourceFilter}
          exclusive
          onChange={(e, val) => { if (val !== null) setSourceFilter(val); }}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="active">Active Feedback</ToggleButton>
          <ToggleButton value="cancellation">Cancellation</ToggleButton>
        </ToggleButtonGroup>

        {sourceFilter !== 'cancellation' && (
          <>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Type</InputLabel>
              <Select value={filterType} label="Type" onChange={(e) => { setFilterType(e.target.value); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="bug">Bug</MenuItem>
                <MenuItem value="suggestion">Suggestion</MenuItem>
                <MenuItem value="question">Question</MenuItem>
                <MenuItem value="compliment">Compliment</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="reviewed">Reviewed</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </>
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>User</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Extras</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getMergedFeedback().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        No feedback found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getMergedFeedback().map((item) => {
                    const isNew = item.status === 'new';
                    const isCancellation = item.source === 'cancellation';
                    const fontWeight = isNew ? 600 : 400;
                    return (
                      <TableRow
                        key={item.id}
                        hover
                        onClick={() => !isCancellation && openDetail(item)}
                        sx={{
                          cursor: isCancellation ? 'default' : 'pointer',
                          bgcolor: isNew ? 'rgba(25, 118, 210, 0.04)' : 'inherit',
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={isCancellation ? 'Cancellation' : 'Active'}
                            size="small"
                            color={isCancellation ? 'error' : 'primary'}
                            variant={isCancellation ? 'filled' : 'outlined'}
                            sx={{ fontSize: 11 }}
                          />
                        </TableCell>
                        <TableCell>
                          {isCancellation ? (
                            <Chip
                              label={REASON_LABELS[item.cancellation_reason] || item.cancellation_reason || 'N/A'}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight }}
                            />
                          ) : (
                            <Chip label={TYPE_LABELS[item.type] || item.type} size="small" variant="outlined" sx={{ fontWeight }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: { xs: 150, sm: 200, md: 300 } }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight }}>
                            {item.displayMessage}
                          </Typography>
                          {isCancellation && item.subscription_started_at && (
                            <Typography variant="caption" color="text.secondary">
                              Tenure: {computeTenure(item.subscription_started_at, item.canceled_at)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="body2" sx={{ fontWeight }}>{item.user_name || 'Unknown'}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.user_email}</Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography variant="body2" sx={{ fontWeight }}>
                            {new Date(item.displayDate).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(item.displayDate).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {isCancellation ? (
                            <Chip label="canceled" size="small" color="error" variant="outlined" />
                          ) : (
                            <Chip label={item.status} size="small" color={STATUS_COLORS[item.status] || 'default'} sx={{ fontWeight }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {!isCancellation && (
                            <>
                              {item.attachments?.length > 0 && (
                                <Tooltip title={`${item.attachments.length} attachment(s)`}>
                                  <AttachFileIcon fontSize="small" color="action" />
                                </Tooltip>
                              )}
                              {item.admin_response && (
                                <Tooltip title="Has response">
                                  <ReplyIcon fontSize="small" color="primary" />
                                </Tooltip>
                              )}
                            </>
                          )}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} align="right" onClick={(e) => e.stopPropagation()}>
                          {!isCancellation && (
                            <>
                              {item.status === 'new' && (
                                <IconButton size="small" onClick={() => handleStatusChange(item.id, 'reviewed')} title="Mark reviewed">
                                  <ReviewedIcon />
                                </IconButton>
                              )}
                              {item.status !== 'new' && (
                                <Tooltip title="Mark as unread">
                                  <IconButton size="small" onClick={() => handleStatusChange(item.id, 'new')}>
                                    <UnreadIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {item.status !== 'resolved' && (
                                <IconButton size="small" onClick={() => handleStatusChange(item.id, 'resolved')} title="Mark resolved" color="success">
                                  <ResolvedIcon />
                                </IconButton>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            {sourceFilter !== 'cancellation' && (
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            )}
          </>
        )}
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={!!selectedFeedback} onClose={() => setSelectedFeedback(null)} maxWidth="sm" fullWidth>
        {selectedFeedback && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              Feedback #{selectedFeedback.id}
              <Chip label={TYPE_LABELS[selectedFeedback.type] || selectedFeedback.type} size="small" sx={{ ml: 1 }} />
              <Chip label={selectedFeedback.status} size="small" color={STATUS_COLORS[selectedFeedback.status] || 'default'} sx={{ ml: 1 }} />
            </DialogTitle>
            <DialogContent dividers>

              {/* User Message */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2">
                    {selectedFeedback.user_name || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({selectedFeedback.user_email})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {new Date(selectedFeedback.created_at).toLocaleString()}
                  </Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedFeedback.message}
                  </Typography>
                </Paper>
                {selectedFeedback.attachments?.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {selectedFeedback.attachments.map((att, idx) => (
                      <Chip
                        key={idx}
                        label={att.filename}
                        size="small"
                        icon={isImageType(att.content_type) ? <ImageIcon /> : <FileIcon />}
                        onClick={() => handleAttachmentClick(selectedFeedback.id, att)}
                        clickable
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Admin Response (existing) */}
              {(selectedFeedback.admin_response || selectedFeedback.admin_attachments?.length > 0) && (
                <Box sx={{ mb: 3, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" color="primary">Admin Response</Typography>
                    {selectedFeedback.responded_at && (
                      <Typography variant="caption" color="text.secondary">
                        {new Date(selectedFeedback.responded_at).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                  {selectedFeedback.admin_response && (
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedFeedback.admin_response}
                      </Typography>
                    </Paper>
                  )}
                  {selectedFeedback.admin_attachments?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {selectedFeedback.admin_attachments.map((att, idx) => (
                        <Chip
                          key={idx}
                          label={att.filename}
                          size="small"
                          icon={isImageType(att.content_type) ? <ImageIcon /> : <FileIcon />}
                          onClick={() => handleAttachmentClick(selectedFeedback.id, att)}
                          clickable
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ mb: 3 }} />

              {/* Compose / Edit Response */}
              <Typography variant="subtitle2" gutterBottom>
                Write Response
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (visible to user)
                </Typography>
              </Typography>
              <TextField
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
                multiline
                rows={3}
                fullWidth
                placeholder="Write a response visible to the user..."
                size="small"
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveResponse}
                  disabled={updating}
                >
                  Save Response
                </Button>

                <input
                  type="file"
                  id="admin-attachment-upload"
                  multiple
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => document.getElementById('admin-attachment-upload').click()}
                  disabled={uploading}
                >
                  Attach Files
                </Button>
                {uploadFiles.length > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    onClick={handleUploadAttachments}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : `Upload ${uploadFiles.length}`}
                  </Button>
                )}
              </Box>

              {/* Pending upload files */}
              {uploadFiles.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {uploadFiles.map((file, idx) => (
                    <Chip
                      key={idx}
                      label={file.name}
                      size="small"
                      icon={file.type?.startsWith('image/') ? <ImageIcon /> : <FileIcon />}
                      onDelete={() => setUploadFiles(prev => prev.filter((_, i) => i !== idx))}
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}

              <Divider sx={{ mb: 3 }} />

              {/* Internal Admin Notes */}
              <Typography variant="subtitle2" gutterBottom>
                Internal Notes
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (not visible to user)
                </Typography>
              </Typography>
              <TextField
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Add internal notes..."
                size="small"
                sx={{ bgcolor: 'grey.100' }}
              />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <Select
                    value={selectedFeedback.status}
                    onChange={(e) => handleStatusChange(selectedFeedback.id, e.target.value)}
                    size="small"
                    disabled={updating}
                  >
                    <MenuItem value="new">New</MenuItem>
                    <MenuItem value="reviewed">Reviewed</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => setSelectedFeedback(null)}>Close</Button>
                <Button variant="contained" onClick={handleSaveNotes} disabled={updating}>
                  Save Notes
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Feedback;
